import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import moment from "moment";
import {
  requestGetTimeOffRequestsAction,
  resetGetTimeOffRequestsStateAction,
  requestCancelTimeOffAction,
  resetCancelTimeOffStateAction,
  resetRequestTimeOffStateAction,
  resetUpdateTimeOffStateAction,
  requestGetAllTimeOffRequestsUsingUserIdAction,
  resetGetAllTimeOffRequestsUsingUserIdStateAction,
  requestGetTimeOffByIdAction,
  resetGetTimeOffByIdStateAction,
} from "../../../Redux/TimeOffState/TimeOffActionCreator";
import CustomDataTable from "../../../components/CustomDataTable";
import Loader from "../../../components/Loader";
import Hoc from "../../../components/HOC";
import TimeOffForm from "./TimeOffForm";
import { TimeOffCalendar } from "../../../components/Calendar";
import MuiDatePicker from "../../../components/MuiDatePicker";

import {
  CalendarTodayRounded,
  AddRounded,
  EditRounded,
  CancelRounded,
  VisibilityRounded,
  ViewListRounded,
  CalendarMonthRounded,
  FilterList,
  DateRange,
  Clear,
} from "@mui/icons-material";
import AlertsCard from "../../../components/AlertsCard";
import { FaEye, FaEdit, FaTrash } from "react-icons/fa";
import CustomToolTip from "../../../components/ToolTip";


const TimeOff = () => {
  const dispatch = useDispatch();
  const [selectedTimeOff, setSelectedTimeOff] = useState(null);
  const [modalTitle, setModalTitle] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [viewMode, setViewMode] = useState("table"); // table, calendar
  const [showFilter, setShowFilter] = useState(false);
  const [startDateFilter, setStartDateFilter] = useState(null);
  const [endDateFilter, setEndDateFilter] = useState(null);

  const timeOffRequestsState = useSelector(
    (state) => state.TimeOffListReducer
  );

 

  // Use mock data if no real data is available (for demonstration)
  const requestTimeOffState = useSelector(
    (state) => state.RequestTimeOffRequestReducer
  );
  const cancelTimeOffState = useSelector(
    (state) => state.CancelTimeOffReducer
  );
  const getTimeOffByIdState = useSelector(
    (state) => state.GetTimeOffByIdReducer
  );


  const getAllTimeOffRequestsUsingUserIdState = useSelector(
    (state) => state.GetAllTimeOffRequestsUsingUserIdReducer
  );

  useEffect(() => {
    dispatch(requestGetTimeOffRequestsAction({ 
      page: timeOffRequestsState?.page || 0, 
      size: timeOffRequestsState?.size || 10, 
      sort: 'desc'
    }));
    dispatch(requestGetAllTimeOffRequestsUsingUserIdAction());

  }, [dispatch]);




  useEffect(() => {
    if (
      requestTimeOffState?.response &&
      !requestTimeOffState?.loading &&
      !requestTimeOffState?.error 
    ) {
      toast.success("Time off request submitted successfully!");
      dispatch(resetGetTimeOffRequestsStateAction());
      dispatch(requestGetTimeOffRequestsAction({ 
        page: 0, 
        size: timeOffRequestsState?.size || 10, 
        sort: 'desc'
      }));
      dispatch(requestGetAllTimeOffRequestsUsingUserIdAction());
      setShowModal(false);
    }

    if (requestTimeOffState?.error && !requestTimeOffState?.loading) {
      toast.error(requestTimeOffState?.error?.message?.errmsg);
    }

    // Handle cancel response
    if (
      cancelTimeOffState?.response &&
      !cancelTimeOffState?.loading &&
      !cancelTimeOffState?.error
    ) {
      dispatch(resetCancelTimeOffStateAction());
      toast.success("Time off request cancelled successfully");
      dispatch(resetGetTimeOffRequestsStateAction());
      dispatch(requestGetTimeOffRequestsAction({ 
        page: 0, 
        size: timeOffRequestsState?.size || 10, 
        sort: 'desc' 
      }));
      dispatch(requestGetAllTimeOffRequestsUsingUserIdAction());
    }

    if (cancelTimeOffState?.error && !cancelTimeOffState?.loading) {
      toast.error("Failed to cancel time off request");
      dispatch(resetCancelTimeOffStateAction());
    }
  }, [requestTimeOffState, cancelTimeOffState, dispatch, timeOffRequestsState?.size]);

  const handleAddTimeOff = () => {
    dispatch(resetGetTimeOffByIdStateAction());
    setSelectedTimeOff(null);
    setModalTitle("Request Time Off");
    setShowModal(true);
    // dispatch(openTimeOffModal()); // Action not defined in TimeOffActionCreator
  };

  const handleCalendarDateClick = (date) => {
    setSelectedCalendarDate(date);
    // Ensure date is a moment object
    const clickedDate = moment.isMoment(date) ? date : moment(date);
    
    // Check if there are any time off requests for this date
    const requestsForDate = getAllTimeOffRequestsUsingUserIdState?.response?.filter((request) => {
      const startDate = moment(request.startDate).startOf("day");
      const endDate = moment(request.endDate).endOf("day");
      const clickedMoment = clickedDate.startOf("day");
      return clickedMoment.isSameOrAfter(startDate) && clickedMoment.isSameOrBefore(endDate);
    });

    if (requestsForDate && requestsForDate.length > 0) {
      // Show the first request for this date
      handleViewTimeOff(requestsForDate[0]);
    } else {
      // If no requests for this date, allow creating a new one
      dispatch(resetGetTimeOffByIdStateAction());
      setSelectedTimeOff({ 
        startDate: clickedDate.format("YYYY-MM-DD"),
        endDate: clickedDate.format("YYYY-MM-DD")
      });
      setModalTitle("Request Time Off");
      setShowModal(true);
      // dispatch(openTimeOffModal()); // Action not defined in TimeOffActionCreator
    }
  };

  const handleEditTimeOff = (timeOff) => {
    
    setModalTitle("Edit Time Off Request");
    setShowModal(true);
    dispatch(requestGetTimeOffByIdAction({ id: timeOff.id  }));
    
    // dispatch(openTimeOffModal()); // Action not defined in TimeOffActionCreator
  };

  const handleViewTimeOff = (timeOff) => {
   
    setModalTitle("View Time Off Request");
    setShowModal(true);
    dispatch(requestGetTimeOffByIdAction({ id: timeOff.id }));
    // dispatch(openTimeOffModal()); // Action not defined in TimeOffActionCreator
  };

 

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      case "APPROVED":
        return "bg-green-100 text-green-800";
      case "REJECTED":
        return "bg-red-100 text-red-800";
      case "CANCELLED":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-blue-100 text-blue-800";
    }
  };

  const columns = [
    {
      name: "Start Date",
      selector: (row) => row.startDate,
      // sortable: true,
      cell: (row) => (
        <div className="text-sm text-gray-900">
          {new moment(row.startDate).format("MM-DD-YYYY")}
        </div>
      ),
    },
    {
      name: "End Date",
      selector: (row) => row.endDate,
      // sortable: true,
      cell: (row) => (
        <div className="text-sm text-gray-900">
          {new moment(row.endDate).format("MM-DD-YYYY")}
        </div>
      ),
    },
    {
      name: "Type",
      selector: (row) => row.type,
      // sortable: true,
      cell: (row) => (
        <div className="text-sm text-gray-900 capitalize">
          {row.type?.replace("_", " ")}
        </div>
      ),
    },
    {
      name: "Reason",
      selector: (row) => row.reason,
      // sortable: false,
      cell: (row) => (
        <div 
          className="text-sm text-gray-900 max-w-xs truncate" 
          title={row.reason}
        >
          {row.reason}
        </div>
      ),
      maxWidth: '150px',
    },
    {
      name: "Status",
      selector: (row) => row.status,
      // sortable: true,
      cell: (row) => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
            row.status
          )}`}
        >
          {row.status}
        </span>
      ),
     
    },
    {
      name: "Remarks",
      selector: (row) => row.approvalRemarks,
      // sortable: true,
      cell: (row) => (
        <div 
          className="text-sm text-gray-900 max-w-xs truncate"
          title={row.approvalRemarks || '-'}
        >
          {row.approvalRemarks || '-'}
    
        </div>
        

      ),
      maxWidth: '150px',
    },
    {
      name: "Submitted Date",
      selector: (row) => row.submittedDate,
      // sortable: true,
      cell: (row) => (
        <div className="text-sm text-gray-900">
          {new moment(row.submittedDate).format("MM-DD-YYYY")}
        </div>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleViewTimeOff(row)}
            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
            data-tip 
            data-for="viewTip"
          >
            <FaEye className="text-primary" />
          </button>
          {row.status === "PENDING" && (
            <>
              <button
                onClick={() => handleEditTimeOff(row)}
                className="p-1 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded transition-colors"
                data-tip 
                data-for="editTip"
              >
                <FaEdit className="text-primary" />
              </button>
              <button
                onClick={() => handleCancelTimeOff(row)}
                disabled={cancelTimeOffState?.loading}
                className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-tip 
                data-for="cancelTip"
              >
                <FaTrash className="text-red-500" />
              </button>
            </>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const handleCancelTimeOff = (timeOff) => {
    Swal.fire({
      title: "Cancel Time Off Request",
      text: `Are you sure you want to cancel this time off request from ${new moment(timeOff.startDate).format("MM-DD-YYYY")} to ${new moment(timeOff.endDate).format("MM-DD-YYYY")}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, cancel it!",
      cancelButtonText: "No, keep it",
      reverseButtons: true,
      customClass: {
        popup: "rounded-lg",
        confirmButton: "rounded-lg",
        cancelButton: "rounded-lg"
      }
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(requestCancelTimeOffAction({ id: timeOff.id }));
      }
    });
  };

  const handlePageChange = (page) => {
      dispatch(requestGetTimeOffRequestsAction({ 
        page: page, 
        size: timeOffRequestsState?.size || 10,
        startDate: timeOffRequestsState?.startDate || null,
        endDate: timeOffRequestsState?.endDate || null,
        sort: 'desc'
    }));
  };
  
  const handleRowsChange = (size) => {
    dispatch(requestGetTimeOffRequestsAction({ 
      page: timeOffRequestsState?.page || 0, 
      size: size,
      startDate: timeOffRequestsState?.startDate || null,
      endDate: timeOffRequestsState?.endDate || null,
      sort: 'desc'
    }));
  };

  const handleFilterSubmit = () => {
    // Validate that both dates are provided
    if (!startDateFilter || !endDateFilter) {
      toast.error("Both Start Date and End Date are required to apply filters");
      return;
    }
    
    // Reset to page 0 when applying filters
    dispatch(requestGetTimeOffRequestsAction({ 
      page: timeOffRequestsState?.page || 0, 
      size: timeOffRequestsState?.size || 10,
      startDate: startDateFilter ? startDateFilter.format("YYYY-MM-DD") : null,
      endDate: endDateFilter ? endDateFilter.format("YYYY-MM-DD") : null,
      sort: 'desc'
    }));
    // Keep filters visible to show which filters are active
    // setShowFilter(false);
  };

  const handleClearFilter = () => {
    setStartDateFilter(null);
    setEndDateFilter(null);
    dispatch(requestGetTimeOffRequestsAction({ 
      page: 0, 
      size: timeOffRequestsState?.size || 10,
      startDate: null,
      endDate: null,
      sort: 'desc'
    }));
    setShowFilter(false);
  };

  // Reset the form state when modal closes
  useEffect(() => {
    if (!showModal && selectedTimeOff === null) {
      dispatch(resetGetTimeOffByIdStateAction());
    }
  }, [showModal, selectedTimeOff, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetGetTimeOffRequestsStateAction());
      dispatch(resetCancelTimeOffStateAction());
      dispatch(resetRequestTimeOffStateAction());
      dispatch(resetUpdateTimeOffStateAction());
      dispatch(resetGetTimeOffByIdStateAction());
      dispatch(resetGetAllTimeOffRequestsUsingUserIdStateAction());
    
    };
  }, [dispatch]);

  return (
    <div className="w-full h-[96vh] sm:h-[85vh] md:h-[92vh] lg:h-[96vh] overflow-y-auto sm:overflow-hidden px-5 py-3">
      {/* Header with Profile and Notifications */}
      <AlertsCard />
      
      {/* Main Content */}
      <div className="flex-1 overflow-y-auto bg-white rounded-xl shadow-sm border border-gray-100">
        <Loader
          isLoading={
            timeOffRequestsState?.loading ||
            cancelTimeOffState?.loading ||
            requestTimeOffState?.loading
          }
        />
        {/* Header */}
        <div className="px-6 py-4 ">
          <div className="flex flex-col space-y-3">
            {/* Top Row: View Toggle and Actions */}
            <div className="flex items-center justify-end">
              <div className="flex items-center space-x-3">
                {/* View Toggle Buttons */}
                <div className="flex items-center bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode("table")}
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      viewMode === "table"
                        ? "bg-white text-indigo-600 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    <ViewListRounded className="h-4 w-4 mr-1.5" />
                    Table
                  </button>
                  <button
                    onClick={() => setViewMode("calendar")}
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      viewMode === "calendar"
                        ? "bg-white text-indigo-600 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    <CalendarMonthRounded className="h-4 w-4 mr-1.5" />
                    Calendar
                  </button>
                </div>
                
                {/* Filter Button */}
                <button
                  onClick={() => setShowFilter(!showFilter)}
                  className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    showFilter || startDateFilter || endDateFilter
                      ? "bg-indigo-600 text-white"
                      : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <FilterList className="h-4 w-4 mr-1.5" />
                  Filter {(startDateFilter || endDateFilter) ? "•" : ""}
                </button>
                
                <button
                  onClick={handleAddTimeOff}
                  className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
                >
                  <AddRounded className="h-4 w-4 mr-2" />
                  Request Time Off
                </button>
              </div>
            </div>

            {/* Filter Panel */}
            {showFilter && (
              <div className="flex items-end gap-3 p-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex-1 grid grid-cols-2 gap-3">
                  <div className="w-full relative">
                    <MuiDatePicker
                      label="Start Date"
                      value={startDateFilter}
                      onChange={(date) => {
                        if (date) {
                          setStartDateFilter(date);
                          // If end date is before start date, update it
                          if (endDateFilter && endDateFilter.isBefore(date)) {
                            setEndDateFilter(date);
                          }
                        } else {
                          setStartDateFilter(null);
                        }
                      }}
                      className="text-sm"
                    />
                  </div>
                  <div className="w-full relative">
                    <MuiDatePicker
                      label="End Date"
                      value={endDateFilter}
                      onChange={(date) => {
                        if (date) {
                          setEndDateFilter(date);
                        } else {
                          setEndDateFilter(null);
                        }
                      }}
                      minDate={startDateFilter || moment()}
                      className="text-sm"
                    />
                  </div>
                </div>
                <button
                  onClick={handleFilterSubmit}
                  disabled={!startDateFilter || !endDateFilter}
                  className={`inline-flex items-center px-4 py-2.5 text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors ${
                    !startDateFilter || !endDateFilter
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-indigo-600 text-white hover:bg-indigo-700"
                  }`}
                >
                  <DateRange className="h-4 w-4 mr-1.5" />
                  Apply
                </button>
                <button
                  onClick={handleClearFilter}
                  className="inline-flex items-center px-4 py-2.5 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-colors"
                >
                  <Clear className="h-4 w-4 mr-1.5" />
                  Clear
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div className="p-4">
          {timeOffRequestsState?.loading ? (
            <div className="flex justify-center items-center py-4">
              <Loader />
            </div>
          ) : (
            <>
              {viewMode === "table" ? (
                <>
                  <CustomDataTable
                  columns={columns}
                  data={timeOffRequestsState?.response?.content || []}
                  pagination={true}
                  serverPagenation={true}
                  paginationTotalRows={timeOffRequestsState?.response?.totalElements}
                  paginationDefaultPage={(timeOffRequestsState?.page || 0) + 1}
                  paginationPerPage={timeOffRequestsState?.size || 10}
                  handleChangePage={handlePageChange}
                  handleRowsChange={handleRowsChange}
                  fixedHeaderScrollHeight={showFilter ? "52vh" : "59vh"}
                  />
                  {/* Render tooltips for actions */}
                  <CustomToolTip id="viewTip" title="View Details" />
                  <CustomToolTip id="editTip" title="Edit Request" />
                  <CustomToolTip id="cancelTip" title="Cancel Request" />
                </>
              ) : (
                <div className={showFilter ? "h-[60vh]" : "h-[70vh]"}>
                  <TimeOffCalendar
                    timeOffRequests={getAllTimeOffRequestsUsingUserIdState?.response || []}
                    onDateClick={handleCalendarDateClick}
                    selectedDate={selectedCalendarDate}
                  />
                </div>
              )}
            </>
          )}
        </div>
      
      </div>

      {/* Time Off Form Modal */}
      {showModal && (
        <TimeOffForm
          title={modalTitle}
          timeOffData={getTimeOffByIdState?.response || null}
          onClose={() => {
            dispatch(resetGetTimeOffByIdStateAction());
            setShowModal(false);
            setSelectedTimeOff(null);
          }}
        />
      )}
    </div>
  );
};

export default Hoc(TimeOff);
